import { TextField } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';

import { updateObject, getProperties } from '../../../store/Objects';
import PropertyField from '../../../components/Properties/PropertyField';
import { updateSinglePageField, getSinglePageData } from '../../../store/SinglePage';
import { formatProperties, getPropertyValue as getPropertyValueUtil } from '../../../utils/propertyFormatter';

const PropertiesSlider = ({ isOpen, onClose, singlePageData, singleId, objectType = 'deals' }) => {
  const dispatch = useDispatch();
  const { currentWorkspace } = useSelector(state => state['Settings']);
  const { properties } = useSelector(state => state['Objects'][objectType]) || {};

  // Local states
  const [searchProperty, setSearchProperty] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});

  // Fetch properties when modal opens
  useEffect(() => { if (isOpen) dispatch(getProperties()) }, [isOpen]);

  // Get all properties for the objectType and format them using the utility function
  const allProperties = formatProperties(properties, objectType);

  // Filter properties based on search and showInDetail flag
  const filteredProperties = allProperties.filter(prop => {
    // Check if property should be shown in detail view
    const showInDetail = prop['displayConfig']?.['showInDetail'] !== false;
    // Check if property matches search
    const matchesSearch = prop['label']?.toLowerCase().includes(searchProperty?.toLowerCase());
    return showInDetail && matchesSearch;
  });

  // Group filtered properties by their group name, excluding Associated Records and System Information
  const groupedProperties = filteredProperties.reduce((acc, prop) => {
    const groupName = prop['group'] || 'Custom Properties';
    // Skip Associated Records and System Information groups
    if (groupName === 'Associated Records' || groupName === 'System Information') return acc;
    if (!acc[groupName]) acc[groupName] = [];
    acc[groupName].push(prop);
    return acc;
  }, {});

  // Sort properties within each group by order
  Object.keys(groupedProperties).forEach(groupName => {
    groupedProperties[groupName].sort((a, b) => {
      // If both have order values, sort by order
      if (a['order'] !== undefined && b['order'] !== undefined) {
        return a['order'] - b['order'];
      }
      // If only one has order, it comes first
      if (a['order'] !== undefined) return -1;
      if (b['order'] !== undefined) return 1;
      // If neither has order, sort alphabetically by label
      return (a['label'] || '').localeCompare(b['label'] || '');
    });
  });

  // Toggle group expansion
  const toggleGroup = (groupName) => setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));

  // Get property value from singlePageData using utility
  const getPropertyValue = (property) => {
    return getPropertyValueUtil(singlePageData, property) || '';
  };

  // Handle property change - similar to index.js
  const handlePropertyChange = (propertyName, newValue) => {
    const property = allProperties.find(p => p.name === propertyName);
    if (!property) return;

    // Prepare update data based on whether it's a custom field
    let updateData;
    if (property.isSystem === false || property.group === 'Custom Properties') {
      // For custom fields, update within customFields object
      updateData = {
        customFields: {
          ...singlePageData?.customFields,
          [propertyName]: newValue
        }
      };
    } else {
      // For system fields, update at root level
      updateData = { [propertyName]: newValue };
    }

    // Update local state immediately
    dispatch(updateSinglePageField(updateData));

    // For multiselect fields, don't update the backend here
    // It will be handled by onSave when the dropdown closes
    if (property.type === 'multiselect') {
      return;
    }

    // For user fields, we need to handle object vs ID
    const dataToSave = property.type === 'user' && typeof newValue === 'object' ? newValue._id : newValue;

    // Prepare backend update data
    let backendData;
    if (property.isSystem === false || property.group === 'Custom Properties') {
      backendData = {
        customFields: {
          ...singlePageData?.customFields,
          [propertyName]: dataToSave
        }
      };
    } else {
      backendData = { [propertyName]: dataToSave };
    }

    dispatch(updateObject({
      id: singleId,
      data: backendData,
      objectType: objectType === 'deal' ? 'deals' : objectType,
    }));
  };

  // Handle property save - for multiselect and other fields that save on close
  const handlePropertySave = (propertyName, newValue) => {
    const property = allProperties.find(p => p.name === propertyName);
    if (!property) return;

    // Prepare update data
    let updateData;
    const dataToSave = property.type === 'user' && typeof newValue === 'object' ? newValue._id : newValue;

    if (property.isSystem === false || property.group === 'Custom Properties') {
      updateData = {
        customFields: {
          ...singlePageData?.customFields,
          [propertyName]: dataToSave
        }
      };
    } else {
      updateData = { [propertyName]: dataToSave };
    }

    // Update in backend
    dispatch(updateObject({
      objectType: objectType === 'deal' ? 'deals' : objectType,
      id: singleId,
      data: updateData
    }));
  };

  // Render property field using PropertyField component
  const renderPropertyField = (property) => {
    const value = getPropertyValue(property);

    return (
      <PropertyField
        library='mui'
        mode='display'
        value={value}
        property={property}
        objectType={objectType === 'deal' ? 'deals' : objectType}
        objectId={singleId}
        editable={!property.displayConfig?.readOnly}
        onChange={handlePropertyChange}
        onSave={(fieldName, newValue, needRefresh) => {
          if (needRefresh) {
            // For tags, refresh the entire object data
            dispatch(getSinglePageData({
              id: singleId,
              objectType: objectType === 'deal' ? 'deals' : objectType
            }));
          } else {
            handlePropertySave(fieldName, newValue);
          }
        }}
        additionalData={{ users: currentWorkspace?.members || [] }}
        hideLabel={true}
      />
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      modalClassName='right right-without-bg'
      backdrop={false}
      className='main-modal right right-side-modal properties-slider-modal'
    >
      <ModalHeader toggle={onClose}>
        <h3>All Properties</h3>
      </ModalHeader>
      <ModalBody>
        <div className='properties-slider-content'>
          {/* Search Box */}
          <div className='search-properties'>
            <i className='icon'>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M17.8568 16.2345L13.203 11.5807C14.0782 10.377 14.5958 8.8965 14.5958 7.2975C14.5958 3.27375 11.322 0 7.2975 0C3.27375 0 0 3.27375 0 7.2975C0 11.322 3.27375 14.595 7.2975 14.595C8.823 14.595 10.2397 14.1247 11.4128 13.3215L16.0912 18L17.8568 16.2345ZM2.1405 7.2975C2.1405 4.4535 4.45425 2.13975 7.29825 2.13975C10.1423 2.13975 12.456 4.4535 12.456 7.2975C12.456 10.1415 10.1423 12.4552 7.29825 12.4552C4.4535 12.4552 2.1405 10.1415 2.1405 7.2975Z" fill="#89868D" />
              </svg>
            </i>
            <TextField
              fullWidth
              hiddenLabel
              size='small'
              variant='standard'
              value={searchProperty}
              placeholder={`Search ${objectType} properties...`}
              onChange={(e) => setSearchProperty(e['target']['value'])}
            />
          </div>

          {/* Property Groups */}
          <div className='property-groups'>
            {Object.entries(groupedProperties).map(([groupName, properties]) => (
              <div key={groupName} className="property-group">
                <div className="group-header" onClick={() => toggleGroup(groupName)}>
                  <h4>{groupName}</h4>
                  <i className={`fa fa-chevron-${expandedGroups[groupName] === false ? 'down' : 'up'}`}></i>
                </div>

                {(expandedGroups[groupName] !== false) && (
                  <div className="group-properties">
                    {properties.map((property) => (
                      <div key={property.name} className="property-item">
                        <div className="property-label">
                          <span>{property.label}</span>
                          {property['isRequired'] && <span className="required">*</span>}
                        </div>
                        <div className="property-value-container">
                          {renderPropertyField(property)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Empty State */}
          {Object.keys(groupedProperties).length === 0 && <div className="empty-state"><p>No properties found matching your search.</p></div>}
        </div>
      </ModalBody>
    </Modal>
  )
}

export default PropertiesSlider